/* ============================================================================
   olmia-opstelling.js — the "Stel je opstelling samen" configurator on
   /pages/opstelling (ported from the design assets/js/bundle.js
   BundleBuilder.create({picker:true, roi:true}) + assets/js/roi.js).

   Owns: cobot selection (payload quick-filter + cobot cards), per-step option
   selection, COMPATIBILITEIT (welke optie past bij welke cobot), de aanbevolen
   opstelling per cobot, de lopende TOTAALPRIJS, de "Jouw opstelling" samenvatting,
   de terugverdientijd (ROI), deeplink-prefill (?cobot=&gripper=&vision=&base=
   &service=) en de "Voeg toe" -> POST /cart/add.js waarbij de gekozen cobotvariant
   en elke gekozen componentvariant worden toegevoegd, elk met een
   {"_opstelling": "<cobot title> opstelling"} regel-eigenschap.

   COMPATIBILITEIT — sections/opstelling-builder.liquid zet op elke optie
   `data-op-fits`: een spatie-gescheiden lijst cobot-handles waarbij die optie past.
   LEEG betekent "past bij alles" — een lege beperking mag nooit iets verbergen.
   Zodra de bezoeker een cobot kiest wordt elke optie die niet in die lijst staat
   geblokkeerd (zichtbaar, doorgestreept, met uitleg) of verborgen — instelbaar via
   `data-op-mode`. Stond er een niet-passende optie aangevinkt, dan valt de keuze
   terug op de "Geen …"-optie van die stap en zegt een melding waarom.

   PAYLOAD-VANGNET (2026-08-21) — de lijstbron hierboven ging in de praktijk nooit af:
   alle grijpers dragen in `custom.compatible_with` alle merken die in de bouwer staan,
   dus `data-op-fits` bevatte simpelweg álle cobots. De bouwer liet daardoor de OnRobot
   VGP30 (30 kg vacuümgripper) zonder één woord toe op een FANUC CRX-3iA (3 kg payload).
   Daarom staat er nu een tweede, natuurkundige regel naast de lijst: een optie met
   `data-op-payload` (uit `custom.payload_kg`) past niet op een cobot met een LAGERE
   `data-payload`. Die regel hoeft niemand met de hand te vullen en veroudert niet.
   Volgorde van oordelen — zie fitsReason():
     1. staat er een EXPLICIETE merchantlijst (`data-op-fits-by="lijst"`), dan is die
        het eindoordeel: staat de cobot erin, dan past het, punt. De merchant mag het
        vangnet dus overrulen — hij weet iets wat de metavelden niet weten.
     2. anders: de (merk-)lijst moet kloppen ÉN het payload-vangnet moet kloppen.
   Een lege `data-op-fits` blijft "past bij alles"; het vangnet is een aparte haak en
   verandert daar niets aan.

   PAYLOAD-SNELFILTER — de grenzen zijn inclusief aan beide kanten (zie PBUCKETS).
   De gekozen cobot blijft altijd zichtbaar, maar krijgt dan `.is-keep` + het label
   uit `[data-op-keep]`, en telt niet mee in de treffer-teller: een kaart die alleen
   blijft staan omdat hij gekozen is mag niet als treffer worden gelezen.

   PRIJSMUUR — een rij waarvan de server het bedrag heeft WEGGELATEN draagt
   `data-op-gated="1"`. Zo'n rij telt niet als € 0 mee: het bedrag wordt uit het
   totaal gelaten en het totaal krijgt de toevoeging "excl. prijs op aanvraag".
   De ROI zwijgt zolang er een afgeschermd bedrag in de opstelling zit — een
   terugverdientijd op een half bedrag is een verkeerd getal, geen indicatie.
   Deze JS kent geen enkel bedrag: alles komt uit data-price van de server.

   Bedragen lopen door OlmiaUI.Money — de enige geldformatter van het thema
   (nl-NL EUR, centen alleen als ze er zijn; zie de PRICE RULE bovenin
   olmia-ui.js). Het lopende totaal wordt in HELE CENTEN opgeteld en pas op het
   eind geformatteerd. Server-gerenderde optie-/cobotprijzen worden uit
   data-money geschilderd door dezelfde formatter.

   Editor-safe (see olmia-editor.js): everything is scoped to the [data-opstelling]
   root and rebuilt inside init(root); the event bindings are guarded (data-op-bound)
   so a re-render rebinds the fresh nodes without stacking handlers. All listeners are
   section-internal (no timers/observers/window), so teardown is a no-op.
   ========================================================================== */
(function () {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  // Vertaalbare teksten (window.olmiaT uit de layout); de tweede parameter is de Nederlandse terugval.
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  const fmt = (s, o) => s.replace(/\{(\w+)\}/g, (m, k) => (k in o ? o[k] : m));

  /* PRICE RULE: nooit een eigen Intl.NumberFormat — zie olmia-ui.js.
     olmia-ui.js wordt NA {{ content_for_layout }} geladen, dus de init onderaan
     dit bestand wacht op DOMContentLoaded voordat er iets geschilderd wordt. */
  const money = (eur) => OlmiaUI.Money.format(eur);
  const cents = (eur) => OlmiaUI.Money.cents(eur);
  const fromCents = (c) => OlmiaUI.Money.fromCents(c);

  const WEEKS_PER_YEAR = 47; // effectieve productieweken (design constant)
  const STEP_KEYS = ['gripper', 'vision', 'base', 'service'];

  /* payload-snelfilter — GRENZEN INCLUSIEF AAN BEIDE KANTEN.
     De chiplabels komen letterlijk uit het ontwerp ("tot 10 kg" / "10–20 kg" /
     "20 kg en meer"); het ontwerp zelf testte met een EXCLUSIEVE bovengrens
     (> 10, > 20) en maakte zijn eigen labels daarmee onwaar: wie op "20 kg en meer"
     klikte kreeg de Dobot CR20A van precies 20 kg niet te zien. `custom.payload_kg`
     is een MAXIMUM, dus een cobot van 20 kg hoort bij "10–20 kg" én bij "20 kg en
     meer" — de overlap op 10 en 20 is de prijs voor labels die kloppen, en die is
     lager dan een cobot die onvindbaar is. */
  const PBUCKETS = {
    all: () => true,
    light: (kg) => kg <= 10,
    mid: (kg) => kg >= 10 && kg <= 20,
    heavy: (kg) => kg >= 20,
  };

  // {cobot} / {n} / {opties} / {totaal} / {kg} / {cobotkg} in een merchant-tekst
  // invullen. Alles gaat via textContent, dus er wordt nooit merchant-HTML uitgevoerd.
  const fill = (tpl, vars) => String(tpl || '').replace(/\{(cobot|cobotkg|kg|n|opties|totaal)\}/g, (m, k) => (vars[k] == null ? '' : String(vars[k])));

  // Een kilogram is geen bedrag: de PRICE RULE (OlmiaUI.Money) geldt hier niet.
  // Wel nl-NL: 2,2 kg, en geen ".0" achter een rond getal.
  const getal = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  const kgTekst = (v) => { const n = getal(v); return n === null ? '' : n.toLocaleString('nl-NL', { maximumFractionDigits: 2 }); };

  function init(sectionRoot) {
    const scope = sectionRoot || document;
    const root = scope.querySelector('[data-opstelling]');
    if (!root) return;

    const cpickWrap = $('[data-cobot-pick]', root);
    const cards = cpickWrap ? $$('.cpick__card', cpickWrap) : [];
    const chips = $$('.pfilter__chip', root);
    const stepsWrap = $('[data-steps]', root);
    const summaryEl = $('[data-summary]', root);
    const totalEl = $('[data-total]', root);
    const savingsEl = $('[data-savings]', root);
    const addBtn = $('[data-bundle-add]', root);
    const alertEl = $('[data-op-alert]', root);
    const countNote = $('[data-op-count-note]', root);
    const keepNote = $('[data-op-keep-note]', root);
    const moreBtn = $('[data-op-more]', root);
    // Kaarten die pas na "Toon alle …" verschijnen. Puur een weergavekap, GEEN
    // filteruitspraak: zodra er wel gefilterd wordt telt alleen het filter.
    const extraCards = cards.filter((c) => c.dataset.opExtra === '1');
    let uitgeklapt = extraCards.length === 0;
    // Een ingeklapte kaart draagt zijn foto in data-op-src/-srcset (zie het snippet):
    // `loading="lazy"` houdt een display:none-foto niet tegen. Zodra de kaart te zien
    // komt gaan de echte attributen erop en haalt de browser hem alsnog lui op.
    const laadFoto = (c) => {
      const img = c.querySelector('img[data-op-src]');
      if (!img) return;
      if (img.dataset.opSrcset) { img.setAttribute('srcset', img.dataset.opSrcset); img.removeAttribute('data-op-srcset'); }
      img.setAttribute('src', img.dataset.opSrc);
      img.removeAttribute('data-op-src');
    };

    // merchant-teksten + modus (alle waarden komen uit de sectie-instellingen)
    const MODE = root.dataset.opMode === 'hide' ? 'hide' : 'block';
    const NOTE = root.dataset.opNote || T('tools.op_note_default', 'Past niet bij {cobot}');
    const HIDDEN_NOTE = root.dataset.opHiddenNote || '';
    const NONE_NOTE = root.dataset.opNoneNote || '';
    const RESET_NOTE = root.dataset.opResetNote || '';
    const GATED_SUFFIX = root.dataset.opGatedSuffix || T('tools.op_gated_suffix', 'excl. prijs op aanvraag');
    const ROI_GATED_NOTE = root.dataset.opRoiGatedNote || '';
    const PAYLOAD_NOTE = root.dataset.opPayloadNote || '';
    const COUNT_TPL = root.dataset.opCountTpl || '';
    const KEEP_TPL = root.dataset.opKeepTpl || '';
    const MORE_TPL = root.dataset.opMoreTpl || '';
    const LESS_TPL = root.dataset.opLessTpl || '';
    const COBOT_TOTAL = getal(root.dataset.opTotal) || 0;

    let currentTotal = 0;
    let currentGated = 0;

    const activeCard = () => cpickWrap && (cpickWrap.querySelector('.cpick__card.active') || cards[0]);
    const selectedOptions = () => (stepsWrap ? $$('.bopt.active', stepsWrap) : []);
    const isGated = (el) => !!(el && el.dataset.opGated === '1');
    // De poorttekst die de server al in de rij heeft gezet ("Log in voor prijzen" /
    // "Prijs op aanvraag"). Nooit zelf een label verzinnen.
    const gateText = (el) => {
      const g = el && el.querySelector('.price__login');
      return (g && g.textContent.trim()) || GATED_SUFFIX;
    };

    /* ---------------- compatibiliteit ------------------------------------ */
    // null = geen beperking ingevuld = past bij alles.
    function fitsOf(el) {
      const raw = (el.dataset.opFits || '').trim();
      return raw ? raw.split(/\s+/) : null;
    }

    /* Waarom past deze optie NIET bij deze cobot?
         ''        → hij past wel
         'lijst'   → hij staat niet in de cobotlijst (blokinstelling of merkregel)
         'payload' → hij vraagt meer kilo's dan de cobot draagt
       De volgorde is bewust: een EXPLICIETE merchantlijst is het eindoordeel en
       overrulet het payload-vangnet; een merkregel niet, want die is afgeleid van
       merknamen en zegt niets over kilo's. */
    function fitsReason(el, card) {
      const handle = (card && card.dataset.cobot) || '';
      const fits = fitsOf(el);
      if (fits && (!handle || fits.indexOf(handle) === -1)) return 'lijst';
      if (el.dataset.opFitsBy === 'lijst') return '';
      const optKg = getal(el.dataset.opPayload);
      const cobotKg = getal(card && card.dataset.payload);
      if (optKg !== null && cobotKg !== null && optKg > cobotKg) return 'payload';
      return '';
    }

    function applyCompat() {
      const moved = [];
      if (!stepsWrap) return moved;
      const card = activeCard();
      const title = (card && card.dataset.title) || '';
      const cobotKg = (card && card.dataset.payload) || '';

      STEP_KEYS.forEach((key) => {
        const labels = $$(`.bopt[data-step="${key}"]`, stepsWrap);
        if (!labels.length) return;
        let blocked = 0;

        labels.forEach((l) => {
          const reden = fitsReason(l, card);
          const ok = !reden;
          if (!ok) blocked += 1;
          l.classList.toggle('is-blocked', !ok);
          l.hidden = !ok && MODE === 'hide';
          if (ok) l.removeAttribute('aria-disabled');
          else l.setAttribute('aria-disabled', 'true');
          // De reden staat in de DOM zodat een suite (en een mens in de inspector)
          // kan zien WELKE regel afging, niet alleen DAT er een regel afging.
          if (ok) l.removeAttribute('data-op-blocked-by');
          else l.setAttribute('data-op-blocked-by', reden);
          const input = $('input', l);
          if (input) input.disabled = !ok;
          const why = $('[data-op-why]', l);
          if (why) {
            // Een te zware optie krijgt de payload-uitleg mét beide gewichten; die is
            // concreet ("Vraagt 30 kg payload; de FANUC CRX-3iA draagt 3 kg") waar
            // "Past niet bij de …" alleen maar herhaalt wat de bezoeker al ziet.
            const tpl = (reden === 'payload' && PAYLOAD_NOTE) ? PAYLOAD_NOTE : NOTE;
            why.textContent = (!ok && MODE !== 'hide')
              ? fill(tpl, { cobot: title, kg: kgTekst(l.dataset.opPayload), cobotkg: kgTekst(cobotKg) })
              : '';
          }
        });

        // Een niet-passende keuze mag niet aangevinkt blijven staan: terug naar de
        // "Geen …"-optie (die is nooit beperkt), anders naar de eerste optie die wél past.
        const active = labels.find((l) => l.classList.contains('active'));
        if (active && active.classList.contains('is-blocked')) {
          const fallback = labels.find((l) => !l.classList.contains('is-blocked') && l.dataset.opt === 'none')
            || labels.find((l) => !l.classList.contains('is-blocked'));
          const label = ($('.bopt__l', active) || {}).textContent || '';
          moved.push(label.trim());
          if (fallback) {
            selectOption(fallback);
          } else {
            active.classList.remove('active');
            const ai = $('input', active);
            if (ai) ai.checked = false;
          }
        }

        // Uitleg per stap: waarom er opties ontbreken (verbergen) of niets past.
        const note = stepsWrap.querySelector(`[data-op-step-note="${key}"]`);
        if (note) {
          let txt = '';
          if (blocked === labels.length) txt = fill(NONE_NOTE, { cobot: title, n: blocked });
          else if (blocked > 0 && MODE === 'hide') txt = fill(HIDDEN_NOTE, { n: blocked, cobot: title });
          note.textContent = txt;
          note.hidden = !txt;
        }
      });
      return moved;
    }

    // Aanbevolen opstelling van deze cobot ("data-op-presets" = "gripper=handle …").
    function applyPresets(card, skip) {
      if (!card || !stepsWrap) return;
      const raw = (card.dataset.opPresets || '').trim();
      if (!raw) return;
      raw.split(/\s+/).forEach((pair) => {
        const i = pair.indexOf('=');
        if (i < 1) return;
        const key = pair.slice(0, i);
        const want = pair.slice(i + 1);
        if (!want || (skip && skip.has(key))) return;
        const labels = $$(`.bopt[data-step="${key}"]`, stepsWrap);
        // Een eigen keuze van de bezoeker blijft staan; alleen een stap die nog op de
        // "Geen …"-optie staat (of net is teruggezet) krijgt de aanbeveling.
        const active = labels.find((x) => x.classList.contains('active'));
        if (active && active.dataset.opt !== 'none') return;
        const opt = labels.find((x) => x.dataset.opt === want);
        if (opt && !opt.classList.contains('is-blocked')) selectOption(opt);
      });
    }

    function announce(moved) {
      if (!alertEl) return;
      const card = activeCard();
      if (!moved.length || !RESET_NOTE) {
        alertEl.textContent = '';
        alertEl.hidden = true;
        return;
      }
      alertEl.textContent = fill(RESET_NOTE, { cobot: (card && card.dataset.title) || '', opties: moved.join(', '), n: moved.length });
      alertEl.hidden = false;
    }

    // Eén doorloop na een cobotwissel: blokkeren → aanbevolen keuzes → opnieuw
    // blokkeren (een aanbeveling kan zelf niet passen) → totaal herrekenen.
    function syncCobot(skipPresets) {
      // De eerste doorloop zet een niet-passende keuze al terug; wat daar is
      // teruggezet moet de bezoeker óók te lezen krijgen, dus beide doorlopen
      // tellen mee voor de melding.
      const eerste = applyCompat();
      applyPresets(activeCard(), skipPresets);
      const tweede = applyCompat();
      announce([...new Set([...eerste, ...tweede])].filter(Boolean));
      // De markering "je keuze valt buiten dit filter" hangt aan de ACTIEVE kaart,
      // dus na elke cobotwissel moet het filter opnieuw gewogen worden.
      applyFilter(huidigeChip());
      update();
    }

    /* ---------------- running total + "Jouw opstelling" summary ----------- */
    function update() {
      const card = activeCard();
      const baseGated = isGated(card);
      // Alles in HELE CENTEN (PRICE RULE): een som van afgeronde euro-bedragen
      // is niet hetzelfde getal als het afgeronde totaal.
      const baseCents = baseGated ? 0 : cents(card && card.dataset.price);
      const baseName = (card && card.dataset.title) || '';
      let totalCents = baseCents;
      let gated = baseGated ? 1 : 0;
      const rows = [
        `<div class="flex" style="justify-content:space-between"><span><b>${baseName}</b></span><span>${baseGated ? gateText(card) : fromCents(baseCents)}</span></div>`,
      ];
      selectedOptions().forEach((l) => {
        if (!l.dataset.variant) return; // "Geen …" zero option contributes niets
        const label = $('.bopt__l', l)?.textContent || '';
        if (isGated(l)) {
          gated += 1;
          rows.push(`<div class="flex" style="justify-content:space-between"><span class="muted">${label}</span><span>${gateText(l)}</span></div>`);
          return;
        }
        const priceCents = cents(l.dataset.price);
        totalCents += priceCents;
        rows.push(`<div class="flex" style="justify-content:space-between"><span class="muted">${label}</span><span>${fromCents(priceCents)}</span></div>`);
      });
      if (summaryEl) summaryEl.innerHTML = rows.join('');
      if (totalEl && totalEl.firstChild) totalEl.firstChild.textContent = fromCents(totalCents);
      // Afgeschermd bedrag wordt WEGGELATEN, niet als € 0 meegeteld — en dat staat erbij.
      if (savingsEl) savingsEl.textContent = gated ? ` ${GATED_SUFFIX}` : '';
      currentTotal = totalCents / 100;   // ROI rekent in euro's, niet in centen
      currentGated = gated;
      refreshRoi();
    }

    /* ---------------- payload quick-filter -------------------------------- */
    /* De gekozen cobot blijft zichtbaar ook als hij buiten het filter valt — de hele
       bouwer eronder rust op die keuze, dus hem wegfilteren verbergt de staat. Maar
       hij is dan géén treffer: hij krijgt `.is-keep`, het label uit [data-op-keep] en
       een aparte regel eronder, en hij telt niet mee in de teller. Anders is een
       kaart die het filter juist NIET haalt niet van een echte treffer te
       onderscheiden — precies de klacht van 21 augustus. */
    const huidigeChip = () => {
      const aan = chips.find((c) => c.classList.contains('is-on'));
      return (aan && aan.dataset.payload) || 'all';
    };

    function applyFilter(key) {
      const test = PBUCKETS[key] || PBUCKETS.all;
      const card = activeCard();
      const activeHandle = card && card.dataset.cobot;
      const ongefilterd = !PBUCKETS[key] || key === 'all';
      let treffers = 0;
      let buiten = null;

      // De inklapkap ("Toon alle …") geldt alléén als er niet gefilterd wordt: wie
      // filtert wil alle treffers zien, niet de eerste acht ervan.
      const kapt = ongefilterd && !uitgeklapt && extraCards.length > 0;

      cards.forEach((c) => {
        const kg = getal(c.dataset.payload);
        const past = test(kg === null ? 0 : kg);
        const gekozen = !!activeHandle && c.dataset.cobot === activeHandle;
        if (past) treffers += 1;
        if (!past && gekozen) buiten = c;
        // `hidden` zegt hier ALLEEN "het filter sluit deze cobot uit". De inklapkap
        // gaat via een klasse op de wrapper (zie de sectie-CSS), zodat de twee
        // niet door elkaar lopen en allebei apart meetbaar blijven.
        c.hidden = !(past || gekozen);
        c.classList.toggle('is-keep', !past && gekozen);
        const badge = $('[data-op-keep]', c);
        if (badge) badge.hidden = past || !gekozen;
        // Onder de kap valt alleen een EXTRA kaart weg, en nooit de gekozen kaart.
        if (!c.hidden && !(kapt && c.dataset.opExtra === '1' && !gekozen)) laadFoto(c);
      });

      if (cpickWrap) cpickWrap.classList.toggle('is-kap', kapt);

      // Teller: bij "Alle payloads" zegt de knop hieronder al hoeveel er zijn.
      if (countNote) {
        const txt = (ongefilterd || !COUNT_TPL) ? '' : fill(COUNT_TPL, { n: treffers, totaal: COBOT_TOTAL || cards.length });
        countNote.textContent = txt;
        countNote.hidden = !txt;
      }
      if (keepNote) {
        const txt = (buiten && KEEP_TPL)
          ? fill(KEEP_TPL, { cobot: buiten.dataset.title || '', kg: kgTekst(buiten.dataset.payload) })
          : '';
        keepNote.textContent = txt;
        keepNote.hidden = !txt;
      }
      if (moreBtn) {
        const tpl = uitgeklapt ? LESS_TPL : MORE_TPL;
        moreBtn.textContent = fill(tpl, { totaal: COBOT_TOTAL || cards.length, n: treffers });
        moreBtn.setAttribute('aria-expanded', uitgeklapt ? 'true' : 'false');
        moreBtn.hidden = !extraCards.length || !ongefilterd || !tpl;
      }
    }

    /* ---------------- terugverdientijd (ROI) ------------------------------ */
    // Haken zijn data-attributen, geen id's: roi/roiHours/roiRate/roiOut/roiSub zijn
    // globale namen die ook in product-bundle-builder en product-roi voorkomen.
    const roiBox = $('[data-op-roi]', root);
    function refreshRoi() {
      if (!roiBox) return;
      const hours = Math.max(0, parseFloat($('[data-op-roi-hours]', roiBox)?.value) || 0);
      const rate = Math.max(0, parseFloat($('[data-op-roi-rate]', roiBox)?.value) || 0);
      const out = $('[data-op-roi-out]', roiBox), sub = $('[data-op-roi-sub]', roiBox);
      if (!out || !sub) return;
      if (currentGated) {
        out.textContent = T('tools.op_roi_gated', 'afgeschermd');
        sub.textContent = ROI_GATED_NOTE;
        return;
      }
      if (!hours || !rate || !currentTotal) {
        out.textContent = T('common.unknown_yet', 'nog onbekend');
        sub.textContent = T('tools.op_roi_fill', 'Vul uren en uurkosten in voor een indicatie.');
        return;
      }
      const yearly = hours * rate * WEEKS_PER_YEAR;
      const months = currentTotal / (yearly / 12);
      out.textContent = months < 1 ? T('tools.op_roi_lt_month', '< 1 maand')
        : fmt(T('tools.op_roi_months', '≈ {n} maanden'), { n: months < 10 ? (Math.round(months * 10) / 10).toLocaleString('nl-NL') : Math.round(months) });
      sub.textContent = fmt(T('tools.op_roi_saving', 'bij {amount} besparing per jaar'), { amount: money(yearly) });
    }

    /* ---------------- "Voeg toe": één POST /cart/add.js ------------------- */
    async function addAll() {
      const card = activeCard();
      if (!card || !card.dataset.variant) return;
      const props = { _opstelling: `${card.dataset.title} opstelling` };
      const items = [{ id: Number(card.dataset.variant), quantity: 1, properties: { ...props } }];
      selectedOptions().forEach((l) => {
        if (l.dataset.variant && !l.classList.contains('is-blocked')) items.push({ id: Number(l.dataset.variant), quantity: 1, properties: { ...props } });
      });
      if (!items.length) return;
      if (addBtn) addBtn.disabled = true;
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) throw await r.json();
        if (window.OlmiaUI) OlmiaUI.toast(T('common.setup_added', 'Opstelling toegevoegd aan winkelwagen'));
        if (window.OlmiaCart) { await OlmiaCart.refresh(); OlmiaCart.open(); }
      } catch (err) {
        console.error('opstelling add failed', err);
        if (window.OlmiaUI) OlmiaUI.toast(T('common.add_failed', 'Kon niet toevoegen aan winkelwagen'));
      } finally {
        if (addBtn) addBtn.disabled = false;
      }
    }

    // pick one option label within its step (single-select radio group)
    function selectOption(label) {
      const input = $('input', label);
      const name = input ? input.name : null;
      $$('.bopt', stepsWrap).forEach((x) => {
        const xi = $('input', x);
        if (name && xi && xi.name === name) { x.classList.remove('active'); xi.checked = false; }
      });
      label.classList.add('active');
      if (input) input.checked = true;
    }

    /* ---------------- events (bound once per root) ------------------------ */
    if (root.dataset.opBound !== '1') {
      root.dataset.opBound = '1';
      if (cpickWrap) {
        cpickWrap.addEventListener('click', (e) => {
          const card = e.target.closest('.cpick__card');
          if (!card) return;
          cards.forEach((c) => c.classList.remove('active'));
          card.classList.add('active');
          syncCobot();
        });
      }
      chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          chips.forEach((c) => c.classList.remove('is-on'));
          chip.classList.add('is-on');
          applyFilter(chip.dataset.payload);
        });
      });
      if (moreBtn) {
        moreBtn.addEventListener('click', () => {
          uitgeklapt = !uitgeklapt;
          applyFilter(huidigeChip());
        });
      }
      if (stepsWrap) {
        stepsWrap.addEventListener('click', (e) => {
          const l = e.target.closest('.bopt');
          if (!l) return;
          // een geblokkeerde optie is niet kiesbaar; de radio staat al op disabled
          if (l.classList.contains('is-blocked')) { e.preventDefault(); return; }
          selectOption(l);
          update();
        });
      }
      if (roiBox) ['[data-op-roi-hours]', '[data-op-roi-rate]'].forEach((sel) => $(sel, roiBox)?.addEventListener('input', refreshRoi));
      if (addBtn) addBtn.addEventListener('click', addAll);
      // JW-belkaart -> open the expert chat (shared chat launcher)
      root.addEventListener('click', (e) => {
        if (e.target.closest('[data-bb-advies]')) document.getElementById('chatLauncher')?.click();
      });
    }

    /* ---------------- deeplink prefill (keuzehulp-contract) --------------- */
    // Wat de keuzehulp expliciet meegeeft wint van de aanbevolen opstelling van de cobot.
    const fromLink = new Set();
    (function prefill() {
      const q = new URLSearchParams(location.search);
      const cobot = q.get('cobot');
      if (cobot) {
        const card = cards.find((c) => c.dataset.cobot === cobot);
        if (card) {
          cards.forEach((c) => c.classList.remove('active'));
          card.classList.add('active');
          // Wijst de keuzehulp een cobot aan die achter "Toon alle …" zat, dan hoort
          // de lijst open te staan; anders wijst de bouwer naar iets onzichtbaars.
          if (card.dataset.opExtra === '1') uitgeklapt = true;
        }
      }
      STEP_KEYS.forEach((key) => {
        const val = q.get(key);
        if (!val || !stepsWrap) return;
        const opt = $$(`.bopt[data-step="${key}"]`, stepsWrap).find((x) => x.dataset.opt === val);
        if (opt) { selectOption(opt); fromLink.add(key); }
      });
    })();

    syncCobot(fromLink);
  }

  if (window.OlmiaEditor) OlmiaEditor.register({
    key: 'olmia-opstelling',
    match: (r) => !!(r && r.querySelector && r.querySelector('[data-opstelling]')),
    init, teardown: () => {}
  });

  /* WACHTEN OP OlmiaUI (PRICE RULE, 2026-08-21).
     layout/theme.liquid laadt olmia-ui.js NA {{ content_for_layout }}, dus dit
     sectie-script draait er normaal vóór — bij een defer-script is readyState op
     dat moment al 'interactive', zodat de oude regel hier meteen init() deed en
     OlmiaUI.Money nog niet bestond. Alle defer-scripts zijn klaar op
     DOMContentLoaded; daarop wachten kost niets en is exact het patroon dat
     assets/olmia-cart-page.js al gebruikte. */
  if (window.OlmiaUI) init(document);
  else if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else init(document);
})();
